import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import ROUTES from '~/shared/constants/routes.const'

import { ButtonPrimary } from '~/components/Buttons'

import { useAppDispatch } from '~/redux/hooks'

import MathMagicForUkraineTitle from '~/donation/components/MathMagicForUkraineTitle'
import { resetDonationDetailsActionCreator, setDonationAmountActionCreator } from '~/donation/redux/donation.slice'
import { donationAmount } from '~/donation/constants/sponsors.const'

const DonatePage = (): React.ReactElement => {
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.MD })
  const commonClassName = `border border-blue-strong  rounded md:p-2.5 font-bold h-12 items-center justify-center flex cursor-pointer`
  const priceCommonClassName = `${commonClassName} hover:bg-purple-light`
  const selectedPriceCommonClassName = `${commonClassName} bg-blue-strong text-white`

  const { t } = useTranslation('donation')
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const dispatch = useAppDispatch()

  const donationMaxLimit = 999999.99
  const donationMinLimit = 0.5

  const [customAmount, setCustomAmount] = useState<number>(0)
  const [isContinuePaymentActive, setIsContinuePaymentActive] = useState<boolean>(false)
  const [selectedAmount, setSelectedAmount] = useState<number>(0)

  useEffect(() => {
    dispatch(resetDonationDetailsActionCreator())
  }, [dispatch])

  useEffect(() => {
    if (!selectedAmount) {
      setIsContinuePaymentActive(false)
      dispatch(resetDonationDetailsActionCreator())
    }
  }, [dispatch, selectedAmount])

  const handleCustomAmountInput = (e: ChangeEvent<HTMLInputElement>): void => {
    let amountString = e.target.value

    if (e.target.value) {
      // any previous fixed/custom amount deselected
      setSelectedAmount(0)
    } else {
      // erased inputted custom amount
      setCustomAmount(0)
      setIsContinuePaymentActive(false)
      dispatch(resetDonationDetailsActionCreator())
      return
    }

    // regex to check if the input is a valid number with 2 decimal places
    const regex = new RegExp(`^(?!\\.?$)(?!0\\d)\\d*(\\.\\d{1,2})?$`)

    let amount = parseFloat(amountString)

    if (isNaN(amount)) {
      setCustomAmount(0)
      setIsContinuePaymentActive(false)
      dispatch(resetDonationDetailsActionCreator())
      const { current } = inputRef
      if (current) {
        current.value = customAmount.toString()
      }
      return
    }
    amountString = amount.toString()
    if (amount < donationMinLimit) {
      if (amount && customAmount) {
        const { current } = inputRef
        if (current) {
          current.value = customAmount.toString()
        }
        setSelectedAmount(customAmount)
        setIsContinuePaymentActive(true)
        dispatch(setDonationAmountActionCreator(customAmount))
      } else {
        setIsContinuePaymentActive(false)
        dispatch(resetDonationDetailsActionCreator())
      }
      return
    } else if (amount > donationMaxLimit || !regex.test(amount.toString())) {
      const { current } = inputRef
      if (current) {
        current.value = customAmount.toString()
      }
      amount = customAmount
    }

    // setting valid amount in the states
    setCustomAmount(amount)
    setSelectedAmount(amount)
    setIsContinuePaymentActive(true)
    dispatch(setDonationAmountActionCreator(amount))
  }

  const handleFixedAmountSelection = (amount: number): void => {
    setSelectedAmount(amount)
    setCustomAmount(0)
    setIsContinuePaymentActive(true)
    dispatch(setDonationAmountActionCreator(amount))
    const { current } = inputRef
    if (current) {
      current.value = ''
    }
  }

  const handleConfirmPayment = (): void => {
    dispatch(setDonationAmountActionCreator(selectedAmount))
    router.push(ROUTES.MATHMAGIC_DONATE_CHECKOUT)
  }

  return (
    <section className="flex flex-col gap-2 max-w-128 min-w-73 md:pt-5 mb-11 m-auto p-2">
      <div className="w-full bg-white h-fit m-auto rounded p-3 flex-col justify-center items-center gap-2 inline-flex">
        <h1 className="bg-clip-text text-transparent bg-blue-strong text-h4 md:text-h3 font-bold tracking-wide text-center drop-shadow-lg leading-9">
          {t('donateTo')}
        </h1>
        <MathMagicForUkraineTitle />
      </div>
      <div className="text-center text-white text-xs font-normal leading-7 tracking-wide">
        {t('donatePage.description')}
      </div>
      <div className="flex flex-col w-full bg-white m-auto rounded p-8">
        <div className="w-full p-3 text-sm bg-blue-strong  rounded text-white font-bold">
          {t('donatePage.oneTimeAmount')}
        </div>
        <div className="grid grid-cols-4 gap-2 md:gap-6 text-blue-strong mt-6">
          {donationAmount.map((amount) => (
            <div
              className={
                !customAmount && selectedAmount === amount ? selectedPriceCommonClassName : priceCommonClassName
              }
              key={amount}
              onClick={() => handleFixedAmountSelection(amount)}
            >
              ${amount}
            </div>
          ))}
          <div className="col-span-3 md:col-span-2">
            <div className="absolute h-12 w-5">
              <span className="bg-gradient-to-t from-orange-logo via-pink-logo to-blue-logo h-full w-full rounded-l text-white font-bold text-justify justify-center self-center items-center flex">
                $
              </span>
            </div>
            <input
              className="w-4/5 h-12 no-input-arrows border pl-6 border-blue-strong rounded text-xs md:text-sm text-black break-words placeholder-black-light focus:placeholder-transparent text-center justify-start flex"
              inputMode="numeric"
              max={donationMaxLimit}
              min={donationMinLimit}
              name="customAmountInput"
              onChange={handleCustomAmountInput}
              onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
              placeholder={t('donatePage.otherAmount')}
              ref={inputRef}
              type="number"
            />
          </div>
        </div>
        <div className="h-12 flex justify-end mt-8">
          <ButtonPrimary
            className={`${
              isContinuePaymentActive ? '' : 'cursor-not-allowed'
            } text-xs leading-4 tracking-wider px-8 py-4 rounded self-end ml-auto font-semibold`}
            disabled={!isContinuePaymentActive}
            isFullWidth={isSmallScreen}
            onClick={handleConfirmPayment}
            size="small"
          >
            {t('donatePage.continueToPaymentButton')}
          </ButtonPrimary>
        </div>
      </div>
    </section>
  )
}

export default DonatePage
